// Interactive savings calculator — deliberately CONSERVATIVE estimates.
// The model is fully client-side and documented inline; results are labeled
// estimates (never "expected" or guaranteed) and every figure carries the
// disclaimer. Analytics: calculator_started on first input change,
// calculator_completed once results render from user-modified inputs.
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BatteryCharging, Calculator, CalendarClock, Gauge, Info, Plug, TrendingUp, Wallet } from "../../components/icons";
import {
  DEFAULT_REGION,
  MODEL,
  REGIONS,
  REGION_CODES,
  money,
  region,
  upfrontCost,
  type NetworkCharge,
} from "../../config/finance";
import { track, trackOnce } from "../../lib/analytics";

// Every figure below comes from src/config/finance.json — retuning a market
// or adding a region is a data edit, not a code change.
interface Results {
  tariffAnnual: number;
  tariffApplies: boolean;
  shaveKw: number;
  arbitrageMonthly: number;
  combinedAnnual: number;
  paybackYears: number | null;
}

/** Annual value of shedding 1 kW of agreed power, for the chosen block — or,
 *  when the visitor does not know it, for the blocks peak shaving lowers. */
export function perKwYear(nc: NetworkCharge, block: number | null): number {
  if (block != null) return nc.perKwYear[String(block)] ?? 0;
  return nc.peakBlocks.reduce((sum, b) => sum + (nc.perKwYear[String(b)] ?? 0), 0);
}

export function estimate(
  regionCode: string,
  batteryKwh: number,
  consumptionKwh: number,
  maxPowerKw: number | null,
  agreedPowerKw: number | null,
  tariffBlock: number | null,
): Results {
  const r = region(regionCode);
  const m = MODEL;
  const usable = Math.max(0, batteryKwh) * m.usableDepthOfDischarge;
  const batteryKw = maxPowerKw ?? Math.max(2, batteryKwh / 2);

  const dailyShiftKwh = Math.min(usable, batteryKw * m.cheapWindowHours);
  const netPerKwh = Math.max(
    0,
    r.arbitrageSpreadPerKwh * m.roundTripEfficiency - m.degradationPerKwh,
  );
  const arbitrageMonthly = dailyShiftKwh * netPerKwh * 30 * m.captureFactor;

  // Network charge: how much agreed power peak shaving can actually shed.
  // Bounded by all three physical limits, not just the household's size —
  // a battery that cannot hold the peak for its full duration cannot lower
  // the billed peak at all. Only regions with a measured-peak charge score
  // anything here; the rest carry a networkChargeNote saying why.
  const nc = r.networkCharge;
  const headroomKw =
    agreedPowerKw != null
      ? Math.max(0, agreedPowerKw * m.peakShare)
      : Math.min(2, consumptionKwh / 4000);
  const shaveKw = nc
    ? Math.max(0, Math.min(batteryKw, usable / m.peakWindowHours, headroomKw))
    : 0;
  const tariffAnnual = nc ? shaveKw * perKwYear(nc, tariffBlock) : 0;

  const combinedAnnual = tariffAnnual + arbitrageMonthly * 12;
  const paybackYears =
    combinedAnnual > m.minAnnualBenefitForPayback ? upfrontCost(r) / combinedAnnual : null;
  return {
    tariffAnnual,
    tariffApplies: nc != null,
    shaveKw,
    arbitrageMonthly,
    combinedAnnual,
    paybackYears,
  };
}

function NumField({
  id, label, value, onChange, min, max, optional,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  min: number;
  max: number;
  optional?: boolean;
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        className="input mt-1"
        value={value}
        placeholder={optional ? "—" : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function SavingsCalculator() {
  const { t } = useTranslation();
  const [market, setMarket] = useState(DEFAULT_REGION);
  const [battery, setBattery] = useState("10");
  const [consumption, setConsumption] = useState("5000");
  const [maxPower, setMaxPower] = useState("");
  const [agreedPower, setAgreedPower] = useState("");
  const [tariffBlock, setTariffBlock] = useState(""); // "" = not sure
  const activeRegion = region(market);
  const touched = useRef(false);

  const onTouch = () => {
    if (!touched.current) {
      touched.current = true;
      trackOnce("calculator_started");
    }
  };

  const results = useMemo(() => {
    const b = parseFloat(battery);
    const c = parseFloat(consumption);
    if (!Number.isFinite(b) || b <= 0 || !Number.isFinite(c) || c <= 0) return null;
    const mp = parseFloat(maxPower);
    const ap = parseFloat(agreedPower);
    const blk = parseInt(tariffBlock, 10);
    const r = estimate(
      market,
      Math.min(b, 1000),
      Math.min(c, 100000),
      Number.isFinite(mp) && mp > 0 ? Math.min(mp, 100) : null,
      Number.isFinite(ap) && ap > 0 ? Math.min(ap, 100) : null,
      Number.isFinite(blk) ? blk : null,
    );
    if (touched.current) trackOnce("calculator_completed");
    return r;
  }, [market, battery, consumption, maxPower, agreedPower, tariffBlock]);

  // Not every region is priced in euro (CH), so formatting goes
  // through the region rather than a hardcoded sign.
  const eur = (v: number) => money(activeRegion, v);

  return (
    <section className="max-w-6xl mx-auto px-4 py-14" id="calculator">
      <h2 className="text-2xl font-semibold text-center flex items-center justify-center gap-2">
        <Calculator size={22} />
        {t("landing.calculator.title")}
      </h2>
      <p className="text-muted text-center mt-2 max-w-2xl mx-auto text-sm">
        {t("landing.calculator.sub")}
      </p>
      {/* Says up front what the two mechanisms are, so the numbers below
          arrive with meaning instead of needing to be reverse-engineered. */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 max-w-3xl mx-auto">
        {[
          { key: "how1", icon: <Gauge size={16} /> },
          { key: "how2", icon: <TrendingUp size={16} /> },
        ].map((h) => (
          <div key={h.key} className="flex items-start gap-2.5 text-sm">
            <span className="text-brand mt-0.5 shrink-0">{h.icon}</span>
            <p className="text-muted">{t(`landing.calculator.${h.key}`)}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-2 max-w-4xl mx-auto">
        <div className="card p-5 space-y-3" onInput={onTouch}>
          <div className="flex items-center gap-2 text-sm font-medium">
            <BatteryCharging size={15} />
            {t("landing.calculator.groupSystem", { defaultValue: "Your system" })}
          </div>
          <div>
            <label className="label" htmlFor="calc-market">{t("landing.calculator.market")}</label>
            <select
              id="calc-market"
              className="input mt-1"
              value={market}
              onChange={(e) => {
                onTouch();
                // A region change can invalidate the block choice (blocks are
                // per-region), so drop it rather than price against a table
                // the new region does not have.
                setTariffBlock("");
                setMarket(e.target.value);
              }}
            >
              {REGION_CODES.map((code) => (
                <option key={code} value={code}>{REGIONS[code].label}</option>
              ))}
            </select>
          </div>
          <NumField id="calc-battery" label={t("landing.calculator.battery")}
                    value={battery} onChange={setBattery} min={1} max={1000} />
          <NumField id="calc-power" label={t("landing.calculator.maxPower")}
                    value={maxPower} onChange={setMaxPower} min={1} max={100} optional />
          <div className="flex items-center gap-2 text-sm font-medium pt-2 border-t border-border">
            <Plug size={15} />
            {t("landing.calculator.groupGrid", { defaultValue: "Your grid connection" })}
          </div>
          <NumField id="calc-consumption" label={t("landing.calculator.consumption")}
                    value={consumption} onChange={setConsumption} min={100} max={100000} />
          <NumField id="calc-agreed" label={t("landing.calculator.agreedPower")}
                    value={agreedPower} onChange={setAgreedPower} min={1} max={100} optional />
          {/* Agreed power is billed per tariff block, and the blocks differ by
              ~500x per kW — knowing the block is what turns this from a blend
              into the visitor's actual number. Only shown where the region
              actually has a measured-peak charge. */}
          {activeRegion.networkCharge && (
            <div>
              <label className="label" htmlFor="calc-block">
                {t("landing.calculator.tariffBlock", { defaultValue: "Current tariff block (optional)" })}
              </label>
              <select
                id="calc-block"
                className="input mt-1"
                value={tariffBlock}
                onChange={(e) => {
                  onTouch();
                  setTariffBlock(e.target.value);
                }}
              >
                <option value="">
                  {t("landing.calculator.tariffBlockAny", { defaultValue: "Not sure — assume peak blocks" })}
                </option>
                {Object.keys(activeRegion.networkCharge.perKwYear).map((n) => (
                  <option key={n} value={n}>
                    {t("landing.calculator.tariffBlockN", {
                      defaultValue: "Block {{n}} · {{amount}}/kW per year",
                      n,
                      amount: `${activeRegion.symbol}${activeRegion.networkCharge!.perKwYear[n].toFixed(2)}`,
                    })}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="space-y-3">
          {results ? (
            <>
              {/* Total first: it is the number the visitor came for. The
                  two components sit under it, each saying what it is. */}
              <div className="card p-5 border-good/40 bg-good/5">
                <div className="label flex items-center gap-2">
                  <Wallet size={14} />
                  {t("landing.calculator.combinedResult")}
                </div>
                <div className="text-4xl font-semibold font-mono mt-1 text-good">
                  {eur(results.combinedAnnual)}
                  <span className="text-base text-muted font-sans">/{t("landing.calculator.year")}</span>
                </div>
                <p className="text-xs text-muted mt-2">
                  {t("landing.calculator.totalHint", {
                    defaultValue:
                      "Network-charge savings plus battery arbitrage, after round-trip losses and battery wear.",
                  })}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="card p-4">
                  <div className="label flex items-center gap-2">
                    <Gauge size={14} />
                    {t("landing.calculator.tariffResult")}
                  </div>
                  {results.tariffApplies ? (
                    <>
                      <div className="text-2xl font-semibold font-mono mt-1">
                        {eur(results.tariffAnnual)}
                        <span className="text-sm text-muted font-sans">/{t("landing.calculator.year")}</span>
                      </div>
                      <div className="text-xs text-muted mt-1">
                        {t("landing.calculator.shaveNote", {
                          defaultValue: "from shedding ~{{kw}} kW of agreed power",
                          kw: results.shaveKw.toFixed(1),
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted mt-2">
                      {/* The per-region note is English in finance.json; the
                          translations live under calculator.networkChargeNotes
                          keyed by region code, falling back to that text. */}
                      {activeRegion.networkChargeNote
                        ? t(`landing.calculator.networkChargeNotes.${REGIONS[market] ? market : DEFAULT_REGION}`, {
                            defaultValue: activeRegion.networkChargeNote,
                          })
                        : t("landing.calculator.noCapacityTariff", {
                          defaultValue:
                            "Not modelled for this market — households pay a fixed grid fee that peak shaving cannot reduce.",
                        })}
                    </div>
                  )}
                </div>
                <div className="card p-4">
                  <div className="label flex items-center gap-2">
                    <TrendingUp size={14} />
                    {t("landing.calculator.arbitrageResult")}
                  </div>
                  <div className="text-2xl font-semibold font-mono mt-1">
                    {eur(results.arbitrageMonthly)}
                    <span className="text-sm text-muted font-sans">/{t("landing.calculator.month")}</span>
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {t("landing.calculator.arbitrageHint", {
                      defaultValue: "one charge/discharge cycle a day on day-ahead prices",
                    })}
                  </div>
                </div>
              </div>

              <div className="card p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="label flex items-center gap-2">
                  <CalendarClock size={14} />
                  {t("landing.calculator.payback")}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-semibold font-mono">
                    {results.paybackYears != null
                      ? `${results.paybackYears.toFixed(1)} ${t("landing.calculator.years")}`
                      : "—"}
                  </div>
                  <div className="text-xs text-muted">
                    {t("landing.calculator.paybackHint", {
                      defaultValue: "against {{price}} hardware + {{install}} installation, ex VAT",
                      price: money(activeRegion, activeRegion.hardwarePrice),
                      install: money(activeRegion, activeRegion.installationPrice),
                    })}
                  </div>
                </div>
              </div>

              {/* The assumptions that make "conservative" a checkable claim
                  rather than an adjective. Values come from finance.json. */}
              <p className="text-xs text-muted flex items-start gap-2">
                <Info size={13} className="mt-0.5 shrink-0" />
                <span>
                  {t("landing.calculator.assumptions", {
                    defaultValue:
                      "Assumes {{eff}}% round-trip efficiency, {{dod}}% usable capacity and {{capture}}% of the theoretical best case — no installation captures every opportunity.",
                    eff: Math.round(MODEL.roundTripEfficiency * 100),
                    dod: Math.round(MODEL.usableDepthOfDischarge * 100),
                    capture: Math.round(MODEL.captureFactor * 100),
                  })}
                </span>
              </p>
              <p className="text-xs text-muted">
                {t("landing.calculator.estimateLabel")} {t("landing.calculator.disclaimer")}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to="/register"
                  className="btn-primary"
                  onClick={() => track("hero_cta_click", { source: "calculator" })}
                >
                  {t("landing.calculator.ctaEstimate")}
                </Link>
                <a
                  href="#tech-specs"
                  className="btn-ghost"
                  onClick={() => track("compatibility_cta_click")}
                >
                  {t("landing.calculator.ctaCompat")}
                </a>
              </div>
            </>
          ) : (
            <div className="card p-6 text-sm text-muted">{t("landing.calculator.invalid")}</div>
          )}
        </div>
      </div>
    </section>
  );
}
